import {
  AssetsMdmStatus,
  Device as DeviceDto,
  MdmDevicesMetrics as MdmDevicesMetricsDto,
  MdmDevicesMetricsResponse as MdmDevicesMetricsResponseDto,
  EnrollmentBreakdown as EnrollmentBreakdownDto,
  OsTypeEnrollmentBreakdown as OsTypeEnrollmentBreakdownDto,
  OsVersionDetail as OsVersionDetailDto,
  DevicesMetricsResponse as DevicesMetricsResponseDto,
  EmployeesMetrics as EmployeesMetricsDto,
  ActiveEmployees as ActiveEmployeesDto,
  InactiveEmployees as InactiveEmployeesDto,
  UnknownEmployees as UnknownEmployeesDto,
  ActiveDevices as ActiveDevicesDto,
  UnassignedDevices as UnassignedDevicesDto,
  AssignedToActiveEmployees as AssignedToActiveEmployeesDto,
  AssignedToInactiveEmployees as AssignedToInactiveEmployeesDto,
} from '@electricjs/core_entity-client';

import { TagTypes, apiSlice } from '@/redux/slices/apiSlice';
import { assetApiSlice } from '@/redux/slices/assetApiSlice';
import {
  ActiveDevicesMetrics,
  ActiveEmployees,
  CreateDeviceArgs,
  DeleteDeviceArgs,
  DeleteTestDeviceArgs,
  Device,
  DevicesMetricsResponse,
  EmployeesMetrics,
  EnrollmentBreakdown,
  GetDeviceMetricsArgs,
  InactiveEmployees,
  IssueEmailArgs,
  MdmDevicesMetrics,
  MdmDevicesMetricsResponse,
  MdmEnrollmentMetrics,
  OsTypeEnrollmentBreakdown,
  OsVersionDetail,
  UnknownEmployees,
  UpdateDeviceArgs,
} from '@/types/devices';
import { LockArgs, WipeArgs } from '@/types/mdmManagement';

const transformDevice = (device: DeviceDto): Device => {
  return {
    id: device.id,
    organizationId: device.organization_id,
    serialNumber: device.serial_number,
    hardwareModel: device.hardware_model,
    osType: device.os_type,
    lockStatus: device.lock_status,
    lockPin: device.lock_pin,
    mdmEnrolledAt: device.mdm_enrolled_at,
    assetId: device.asset_id,
    electricAppInstalledAt: device.electric_app_installed_at,
  };
};

const transformOsVersionDetail = (
  dto: OsVersionDetailDto
): OsVersionDetail => ({
  osName: dto.os_name,
  osVersion: dto.os_version,
  count: dto.count,
});

const transformOsTypeEnrollmentBreakdown = (
  dto: OsTypeEnrollmentBreakdownDto
): OsTypeEnrollmentBreakdown => ({
  total: dto.total ?? 0,
  versions: dto.versions ?? 0,
  atEol: dto.at_eol ?? 0,
  nearEol: dto.near_eol ?? 0,
  supported: dto.supported ?? 0,
  unknown: dto.unknown ?? 0,
  versionDetails: dto.version_details?.map(transformOsVersionDetail) ?? [],
});

const transformEnrollmentBreakdown = (
  dto: EnrollmentBreakdownDto
): EnrollmentBreakdown => ({
  darwin: transformOsTypeEnrollmentBreakdown(dto.darwin),
  windows: transformOsTypeEnrollmentBreakdown(dto.windows),
});

const transformMdmDevicesMetrics = (
  dto: MdmDevicesMetricsDto
): MdmDevicesMetrics => ({
  managedDevices: dto.managed_devices,
  unmanagedDevices: dto.unmanaged_devices,
  unmanagedDevicesIds: dto.unmanaged_devices_ids,
  assignedEnrolled: transformEnrollmentBreakdown(dto.assigned_enrolled),
  unassignedEnrolled: transformEnrollmentBreakdown(dto.unassigned_enrolled),
});

const transformMdmDevicesMetricsResponse = (
  dto: MdmDevicesMetricsResponseDto
): MdmDevicesMetricsResponse => ({
  devices: transformMdmDevicesMetrics(dto.devices),
  employeesWithoutAssets: dto.employees_without_assets,
});

const transformActiveEmployees = (
  dto: ActiveEmployeesDto
): ActiveEmployees => ({
  total: dto.total,
  withDevices: dto.with_devices,
  withoutDevices: dto.without_devices,
});

const transformInactiveEmployees = (
  dto: InactiveEmployeesDto
): InactiveEmployees => ({
  total: dto.total,
});

const transformUnknownEmployees = (
  dto: UnknownEmployeesDto
): UnknownEmployees => ({
  total: dto.total,
});

const transformEmployeesMetrics = (
  dto: EmployeesMetricsDto
): EmployeesMetrics => ({
  total: dto.total,
  active: transformActiveEmployees(dto.active),
  inactive: transformInactiveEmployees(dto.inactive),
  unknown: transformUnknownEmployees(dto.unknown),
});

const transformMdmEnrollmentMetrics = (
  dto:
    | UnassignedDevicesDto
    | AssignedToActiveEmployeesDto
    | AssignedToInactiveEmployeesDto
): MdmEnrollmentMetrics => ({
  total: dto.total,
  mdmEnrolled: dto.mdm_enrolled,
  mdmUnenrolled: dto.mdm_unenrolled,
});

const transformActiveDevicesMetrics = (
  dto: ActiveDevicesDto
): ActiveDevicesMetrics => ({
  total: dto.total,
  assignedToActiveEmployees: transformMdmEnrollmentMetrics(
    dto.assigned_to_active_employees
  ),
  assignedToInactiveEmployees: transformMdmEnrollmentMetrics(
    dto.assigned_to_inactive_employees
  ),
  unassigned: transformMdmEnrollmentMetrics(dto.unassigned),
});

const transformDevicesMetricsResponse = (
  dto: DevicesMetricsResponseDto
): DevicesMetricsResponse => ({
  employees: transformEmployeesMetrics(dto.employees),
  devices: {
    total: dto.devices.total,
    active: transformActiveDevicesMetrics(dto.devices.active),
    inactive: dto.devices.inactive,
  },
});

export const deviceSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getMyDevices: builder.query<Device[] | null, string>({
      query: organizationId =>
        `/v1/organizations/${organizationId}/employees/me/devices`,
      transformResponse: (devicesResponse: DeviceDto[]) => {
        if (devicesResponse.length === 0) {
          return null;
        }
        return devicesResponse.map(device => transformDevice(device));
      },
      providesTags: [TagTypes.Device],
    }),
    updateDevice: builder.mutation<Device, UpdateDeviceArgs>({
      query: ({
        organizationId,
        deviceId,
        lockStatus,
        lockPin,
        mdmEnrolledAt,
        electricAppInstalledAt,
        assignedTo,
      }) => ({
        url: `/v1/organizations/${organizationId}/devices/${deviceId}`,
        method: 'PATCH',
        body: {
          lock_status: lockStatus,
          lock_pin: lockPin,
          mdm_enrolled_at: mdmEnrolledAt,
          electric_app_installed_at: electricAppInstalledAt,
          assigned_to: assignedTo,
        },
      }),
      transformResponse: (device: DeviceDto) => transformDevice(device),
      invalidatesTags: (_result, _error, args) => [
        {
          type: TagTypes.EmployeeWithDevice,
          id: args.employeeId,
        },
        {
          type: TagTypes.Asset,
        },
      ],
    }),
    sendIssueEmail: builder.mutation<void, IssueEmailArgs>({
      query: ({ employeeDeviceOsGrouping, issueType, organizationId }) => ({
        url: `v1/devices/email`,
        method: 'POST',
        body: {
          employee_device_os_grouping: employeeDeviceOsGrouping,
          issue_type: issueType,
          organization_id: organizationId,
        },
      }),
    }),
    createDevice: builder.mutation<Device, CreateDeviceArgs>({
      query: ({
        organizationId,
        serialNumber,
        hardwareModel,
        osType,
        assignedTo,
        electricAppInstalledAt,
      }) => ({
        url: `/v1/organizations/${organizationId}/devices`,
        method: 'POST',
        body: {
          organization_id: organizationId,
          serial_number: serialNumber,
          hardware_model: hardwareModel,
          os_type: osType,
          electric_app_installed_at: electricAppInstalledAt,
          assigned_to: assignedTo,
        },
      }),
      transformResponse: (device: DeviceDto) => transformDevice(device),
      // TODO: ARCH-2407 https://electricai.atlassian.net/browse/ARCH-2407 - Check if we still need to invalidate using TagTypes.EmployeeWithDevice
      invalidatesTags: (_result, _error, args) => [
        {
          type: TagTypes.EmployeeWithDevice,
          id: args.employeeId,
        },
        {
          type: TagTypes.Asset,
        },
      ],
    }),
    deleteDevice: builder.mutation<void, DeleteDeviceArgs>({
      query: ({ organizationId, deviceId }) => ({
        url: `/v1/organizations/${organizationId}/devices/${deviceId}`,
        method: 'DELETE',
        body: {
          organization_id: organizationId,
        },
      }),
      invalidatesTags: (_result, _error, args) => [
        {
          type: TagTypes.EmployeeWithDevice,
          id: args.employeeId,
        },
        {
          type: TagTypes.Asset,
        },
      ],
    }),
    deleteTestDevice: builder.mutation<void, DeleteTestDeviceArgs>({
      query: ({ organizationId, deviceId }) => ({
        url: `/v1/maintenance/organizations/${organizationId}/test-devices/${deviceId}`,
        method: 'DELETE',
        body: {
          organization_id: organizationId,
        },
      }),
      invalidatesTags: (_result, _error, args) => [
        {
          type: TagTypes.EmployeeWithDevice,
          id: args.employeeId,
        },
        {
          type: TagTypes.Asset,
        },
      ],
    }),
    lockDevice: builder.mutation<void, LockArgs>({
      query: ({ organizationId, deviceId, lockMethod }) => ({
        url: `/v1/organizations/${organizationId}/devices/${deviceId}/lock`,
        method: 'POST',
        body: { lock_method: lockMethod },
      }),
    }),
    wipeDevice: builder.mutation<void, WipeArgs>({
      query: ({ organizationId, deviceId, wipeMethod }) => ({
        url: `/v1/organizations/${organizationId}/devices/${deviceId}/wipe`,
        method: 'POST',
        body: { wipe_method: wipeMethod },
      }),
    }),
    getMdmDeviceMetrics: builder.query<
      MdmDevicesMetricsResponse,
      GetDeviceMetricsArgs
    >({
      query: ({ organizationId }) => ({
        url: `/v1/organizations/${organizationId}/devices/mdm-metrics`,
        method: 'GET',
      }),
      transformResponse: (response: MdmDevicesMetricsResponseDto) =>
        transformMdmDevicesMetricsResponse(response),
      providesTags: [TagTypes.DeviceMetrics],
    }),
    getDeviceMetrics: builder.query<
      DevicesMetricsResponse,
      GetDeviceMetricsArgs
    >({
      query: ({ organizationId }) => ({
        url: `/v1/organizations/${organizationId}/devices/metrics`,
        method: 'GET',
      }),
      transformResponse: (response: DevicesMetricsResponseDto) =>
        transformDevicesMetricsResponse(response),
      providesTags: [TagTypes.DeviceMetrics],
    }),
    updateDeviceMdmStatusOverride: builder.mutation<
      void,
      {
        organizationId: string;
        assetId: string;
        mdmStatusOverride: AssetsMdmStatus | null;
      }
    >({
      query: ({ organizationId, assetId, mdmStatusOverride }) => ({
        url: `/v1/organizations/${organizationId}/assets/${assetId}`,
        method: 'PATCH',
        body: { mdm_status_override: mdmStatusOverride },
      }),
      async onQueryStarted(
        { organizationId, assetId, mdmStatusOverride },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          assetApiSlice.util.updateQueryData(
            'getAsset',
            { organizationId, assetId },
            draft => {
              draft.mdmStatusOverride = mdmStatusOverride ?? undefined;
              if (draft.device) {
                draft.device.mdmStatus =
                  mdmStatusOverride !== null ? mdmStatusOverride : undefined;
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [
        TagTypes.Device,
        TagTypes.DeviceMetrics,
        TagTypes.Asset,
        TagTypes.AssetDetail,
      ],
    }),
  }),
});

// export auto-generated hooks
export const {
  useGetMyDevicesQuery,
  useUpdateDeviceMutation,
  useSendIssueEmailMutation,
  useCreateDeviceMutation,
  useDeleteDeviceMutation,
  useDeleteTestDeviceMutation,
  useGetMdmDeviceMetricsQuery,
  useGetDeviceMetricsQuery,
  useUpdateDeviceMdmStatusOverrideMutation,
  useLockDeviceMutation,
  useWipeDeviceMutation,
} = deviceSlice;
